import React, { useState, useContext, useEffect } from "react";
import { AccountContext } from "../../store/AccountContext";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  Grid,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import MembershipCardForm from "./MembershipCardForm";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import ErrorIcon from "@mui/icons-material/Error";
import RadioButtonUncheckedOutlinedIcon from "@mui/icons-material/RadioButtonUncheckedOutlined";
import usePromise from "react-use-promise";
import { CurrencySymbol } from "@booyaltd/core";

/**
 * PM5 T2 (money path, batch "myaccount-membership") — converted from MUI v4 +
 * `makeStyles` to MUI v5+ (`@mui/material`) `styled`/`sx`.
 * See docs/epics/pm5-mui-v5-migration.md §3 (T2) and docs/mui-conversion-guide.md.
 *
 * SUBSCRIPTIONS ESCALATION CLASS — this is the two-step "choose a membership →
 * pay" flow, and it owns the payment-pending / success / failure screens.
 * Styling-only: no handler, condition, state value, prop or DOM node moved.
 *
 * DEAD RULES DROPPED (guide §1.2 row 1) — declared but never referenced from
 * this file's JSX: `upgradeHeader`, `bodytextSml`, `subscriptionLogo`,
 * `subscriptionHeading`, `cardContainer`, `selectedSubscription`, `center`,
 * `giftcardRedeem`, `cardInputError`, `step2SelectedSubscriptionContainer`,
 * `step2SelectedSubscription`, `changeMembership`, `paymentSuccessWrapper`,
 * `paymentSuccessTick`, `progressButtonContainer`, `membershipBox`,
 * `paymentMethodSelection`, `paymentMethodSelectionDisabled`,
 * `paymentMethodSelector`, `cardLogo`, `paypalLogo` and `changePaymentMethod`.
 * The currency `<Select className={classes.currencySelect}>` referenced a key
 * that does not exist in this file's `useStyles` object, so it resolved to
 * `className={undefined}` and applied nothing.
 */

/* ---------------------------------------------------------------------- *
 * styled() — non-MUI elements that carry a fixed style
 * ---------------------------------------------------------------------- */

// was: useStyles().subscriptionCost — a bare <span>.
const SubscriptionCostSpan = styled("span")(({ theme }) => ({
  color: theme.palette.background.ffNavy,
  fontSize: "1.4rem",
  lineHeight: "1.6rem",
  [theme.breakpoints.up("sm")]: {
    fontSize: "1.8rem",
    lineHeight: "2rem",
  },
  [theme.breakpoints.up("md")]: {
    fontSize: "2.4rem",
    lineHeight: "3.6rem",
  },
}));

// was: useStyles().subscriptionTerm — a bare <span>.
const SubscriptionTermSpan = styled("span")({
  fontWeight: 400,
});

// was: useStyles().selectedMembershipPrice — a bare <span>.
const SelectedMembershipPriceSpan = styled("span")(({ theme }) => ({
  color: theme.palette.background.ffNavy,
  fontWeight: 400,
  fontSize: "0.9rem",
}));

// was: useStyles().selectedMembershipTerm — a bare <span>. `fontSize` is the
// pre-existing typo "selectedMem0.9rem": an invalid length, dropped by the CSS
// parser in v4 and in v9 alike. Carried across verbatim (epic rule 3 — port
// defects, don't fix them in a conversion).
const SelectedMembershipTermSpan = styled("span")(({ theme }) => ({
  color: theme.palette.background.ffNavy,
  fontWeight: 400,
  fontSize: "selectedMem0.9rem",
}));

/* ---------------------------------------------------------------------- *
 * sx — styles on MUI components
 * ---------------------------------------------------------------------- */

/**
 * ♦ NEW DELTA (guide §2). v4's `.MuiGrid-container` declared `width: 100%`;
 * v9's Grid container has NO width rule at all — `generateGridStyles` emits
 * only `display: flex` / `flex-wrap` / `gap`. Harmless for a block-level
 * container, but a container nested DIRECTLY inside another container is a
 * flex item, and `width: auto` makes it shrink-to-fit. Measured in Chromium
 * on the exact emitted CSS: 800px in v4, 32px in v9, restored to 800px by
 * this rule. Restated verbatim on every converted container.
 */
const v4GridContainerWidthSx = { width: "100%" };

/**
 * ♦ v4's `<Grid container spacing={2}>` was
 *   `.MuiGrid-spacing-xs-2      { margin: -8px; width: calc(100% + 16px) }`
 *   `.MuiGrid-spacing-xs-2 > .MuiGrid-item { padding: 8px }`
 * v9 replaces that with `gap` on the container and no padding on the items
 * (guide §2). Carry v4's own declarations verbatim: omit `spacing` (v9 default
 * 0 ⇒ `gap: 0px`) and give each item the 8px padding itself. The spacing
 * width supersedes `.MuiGrid-container`'s `width: 100%`, exactly as in v4.
 */
const v4GridItemSx = { padding: "8px" };

// was: `<Grid container spacing={2} className={classes.paper}>` — the spacing
// declarations first (MUI's own sheet), then `paper` (this file's, which
// injected later and therefore won any shared key; here they are disjoint).
const paperContainerSx = {
  width: "calc(100% + 16px)",
  margin: "-8px",
  display: "flex",
  textAlign: "center",
};

// was: useStyles().upgradeTitle. The literal `@media (min-width:600px)` /
// `(min-width:960px)` become `breakpoints.up("sm"|"md")`; `themeV5` pins those
// to the v4 values (600/960), so the emitted query is identical.
const upgradeTitleSx = (theme) => ({
  color: theme.palette.background.ffNavy,
  fontSize: "2rem",
  textAlign: "center",
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
  [theme.breakpoints.up("sm")]: {
    fontSize: "2.2rem",
    lineHeight: "2.4rem",
  },
  [theme.breakpoints.up("md")]: {
    fontSize: "3rem",
    lineHeight: "3.2rem",
  },
});

// was: useStyles().subtitle
const subtitleSx = (theme) => ({
  color: theme.palette.background.ffNavy,
  fontSize: "1.4rem",
  textAlign: "left",
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
  [theme.breakpoints.up("sm")]: {
    fontSize: "1.6rem",
    lineHeight: "1.8rem",
  },
  [theme.breakpoints.up("md")]: {
    fontSize: "1.6rem",
    lineHeight: "1.8rem",
  },
});

// was: useStyles().thirdTitle
const thirdTitleSx = (theme) => ({
  color: theme.palette.background.ffNavy,
  fontSize: "1.2rem",
  textAlign: "center",
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(2),
  [theme.breakpoints.up("sm")]: {
    fontSize: "1.2rem",
    lineHeight: "1.4rem",
  },
  [theme.breakpoints.up("md")]: {
    marginTop: theme.spacing(3),
    fontSize: "1.6rem",
    lineHeight: "1.8rem",
  },
});

// was: useStyles().bodytext
const bodytextSx = (theme) => ({
  // marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
  color: theme.palette.background.ffNavy,
  fontSize: "0.9rem",
  lineHeight: "1rem",
  textAlign: "left",
  [theme.breakpoints.up("sm")]: {
    fontSize: "0.9rem",
    lineHeight: "1.1rem",
  },
  [theme.breakpoints.up("md")]: {
    fontSize: "0.9rem",
    lineHeight: "1.1rem",
  },
});

// was: useStyles().subscriptionWrapper — used on both a Grid ITEM and a Grid
// CONTAINER, so it is declared once and the container form adds v4's
// `.MuiGrid-container` width on top.
const subscriptionWrapperRules = (theme) => ({
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(2),
  [theme.breakpoints.up("sm")]: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  [theme.breakpoints.up("md")]: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
});
const subscriptionWrapperItemSx = subscriptionWrapperRules;
const subscriptionWrapperContainerSx = (theme) => ({
  ...v4GridContainerWidthSx,
  ...subscriptionWrapperRules(theme),
});

/**
 * was: useStyles().subscriptionContainer / .selectedSubscriptionContainer.
 *
 * ♦ NEW DELTA (guide §2) — JSS `$ruleName` local references. The v4
 * `selectedSubscriptionContainer` carried three nested blocks written with
 * JSS's local-rule syntax:
 *   `"& $subscriptionPrice":       { fontWeight: 600, color: ffNavy }`
 *   `"& $subscriptionName":        { color: ffNavy }`
 *   `"& $subscriptionDescription": { color: ffNavy }`
 * emotion has no `$ref` equivalent, and reproducing them would mean inventing
 * stable class names on children that otherwise need none. They are dropped
 * because each is provably INERT: every declaration is byte-identical to what
 * the referenced rule already sets on the same element (`subscriptionPrice` is
 * `{ fontWeight: 600, color: ffNavy }`; `subscriptionName` and
 * `subscriptionDescription` both already set `color: ffNavy`). The descendant
 * selector only ever re-asserted the child's own value, so the computed style
 * is unchanged in both the selected and unselected branches.
 */
const subscriptionContainerSx = (theme) => ({
  color: theme.palette.background.ffNavy,
  backgroundColor: "#dadada",
  padding: theme.spacing(2),
  display: "inline-block",
  textAlign: "left",
  width: "100%",
  flexBasis: "unset",
  margin: theme.spacing(1, 0),
  "&:hover": {
    backgroundColor: "#e2e2e2",
    cursor: "pointer",
  },
});

const selectedSubscriptionContainerSx = (theme) => ({
  color: theme.palette.common.white,
  backgroundColor: "#f9f9f9",
  padding: theme.spacing(2),
  display: "inline-block",
  width: "100%",
  textAlign: "left",
  margin: theme.spacing(1, 0),
  "&:hover": {
    backgroundColor: "#f9f9f9",
    cursor: "pointer",
  },
});

// was: useStyles().selectButton
const selectButtonSx = (theme) => ({
  backgroundColor: theme.palette.background.ffGreen,
  border: "2px solid #99ca3c",
  color: theme.palette.background.ffNavy,
  padding: "10px 14px",
  fontSize: "0.8rem",
  lineHeight: "1.2rem",
  "&:hover": {
    backgroundColor: theme.palette.background.ffGreen,
    border: "2px solid #99ca3c",
    color: theme.palette.background.ffNavy,
  },
  [theme.breakpoints.up("sm")]: {
    fontSize: "0.9rem",
  },
});

// was: useStyles().selectButtonUnchecked
const selectButtonUncheckedSx = (theme) => ({
  transition: "none",
  backgroundColor: "#ddd",
  border: "2px solid #171717",
  color: "#171717",
  padding: "10px 14px",
  fontSize: "0.8rem",
  lineHeight: "1.2rem",
  "&:hover": {
    border: "2px solid #171717",
  },
  [theme.breakpoints.up("sm")]: {
    fontSize: "0.9rem",
  },
});

// was: useStyles().subscriptionPrice
const subscriptionPriceSx = (theme) => ({
  fontWeight: 600,
  color: theme.palette.background.ffNavy,
});

// was: useStyles().subscriptionName
const subscriptionNameSx = (theme) => ({
  color: theme.palette.background.ffNavy,
  fontSize: "1.4rem",
  fontWeight: 500,
  textAlign: "left",
  [theme.breakpoints.up("sm")]: {
    fontSize: "1.8rem",
    lineHeight: "2rem",
  },
});

// was: useStyles().subscriptionDescription
const subscriptionDescriptionSx = (theme) => ({
  color: theme.palette.background.ffNavy,
  fontSize: "0.9rem",
  lineHeight: "1rem",
  fontWeight: "400",
  textAlign: "left",
  margin: "10px 0 12px 0",
  [theme.breakpoints.up("sm")]: {
    fontSize: "1rem",
    lineHeight: "1rem",
  },
  [theme.breakpoints.up("md")]: {
    fontSize: "1rem",
    lineHeight: "1rem",
  },
});

// was: useStyles().continueButton, plus a v4 default that has to be restored.
//
// PARITY RESTORE (guide §2, "Button default color"): `color: "text.primary"` is
// NOT in the v4 rule. v4's plain <Button> defaulted to `color="default"`, whose
// root style was `color: theme.palette.text.primary`. v5+ deleted `default` and
// made `primary` the default — and this theme's `primary.main` IS the lime that
// `typography.button` paints the button background with, so an uncompensated
// port renders an invisible lime-on-lime "Continue To Payment" button.
const continueButtonSx = (theme) => ({
  width: "100%",
  margin: theme.spacing(2, 0, 0, 0),
  [theme.breakpoints.up("sm")]: {
    width: "auto",
  },
  color: "text.primary",
});

// The same PARITY RESTORE for the three <Button>s that carried no v4 class
// ("Try Again" ×2 and "Get Started").
const buttonColourRestoreSx = { color: "text.primary" };

// was: useStyles().smlDivider
const smlDividerSx = (theme) => ({
  backgroundColor: "#c1c1c1",
  height: "1px",
  width: "100%",
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(2),
});

/**
 * was: `<Box display="flex" className={classes.selectedMembership}>`.
 * Guide §2, "`makeStyles` `className` alongside `Box` system props": the
 * ex-system-prop goes FIRST and the ex-`className` rules after it.
 *
 * `zIndex: "-100000"` is carried across as the same string. `sx`'s `zIndex`
 * is theme-mapped (`themeKey: "zIndex"`), but a miss falls through to the raw
 * value, so emotion emits `z-index: -100000` exactly as JSS did.
 */
const selectedMembershipSx = (theme) => ({
  display: "flex",
  zIndex: "-100000",
  backgroundColor: "#f5f5f5",
  padding: theme.spacing(1.5),
});

// was: useStyles().selectedMembershipTitle
const selectedMembershipTitleSx = (theme) => ({
  textAlign: "left",
  color: theme.palette.background.ffNavy,
  fontWeight: 500,
});

// was: useStyles().selectedMembershipChange
const selectedMembershipChangeSx = (theme) => ({
  color: theme.palette.background.ffGreen,
  fontWeight: 500,
  textDecoration: "underline",
  "&:hover": {
    cursor: "pointer",
  },
});

// was: useStyles().selectedMembershipLabel
const selectedMembershipLabelSx = (theme) => ({
  color: theme.palette.background.ffNavy,
  textAlign: "left",
  fontSize: "1.2rem",
  lineHeight: "1.6rem",
  marginTop: "20px",
  marginBottom: "10px",
  fontWeight: 500,
  fontFamily: ["Roboto", '"Helvetica Neue"', "Arial", "sans-serif"].join(","),
});

// was: useStyles().responseBox
const responseBoxSx = {
  textAlign: "center",
  padding: "10%",
};

// was: useStyles().responseIcon
const responseIconSx = (theme) => ({
  width: "4rem",
  height: "4rem",
  [theme.breakpoints.up("sm")]: {
    width: "5rem",
    height: "5rem",
  },
});

// was: useStyles().divider
const dividerSx = (theme) => ({
  width: "50%",
  margin: "10% 25% 10% 25%",
  height: "1px",
  backgroundColor: "#c1c1c1",
  [theme.breakpoints.up("sm")]: {
    margin: "5% 25% 10px 25%",
  },
  [theme.breakpoints.up("md")]: {
    margin: "5% 25% 10px 25%",
  },
});

// was: useStyles().responseHelp
const responseHelpSx = (theme) => ({
  color: theme.palette.background.ffNavy,
  fontSize: "0.9rem",
  lineHeight: "0.9rem",
  textAlign: "center",
  "& a": {
    color: theme.palette.background.ffNavy,
  },
});

const UpgradeFormNew = (props) => {
  const {
    getSubscriptionProducts,
    subscriptionProducts,
    paymentPending,
    paymentFailure,
    paymentSuccess,
    paymentUnableProcess,
    resetErrors,
    resetPaymentErrors,
    getMembershipDetails,
  } = useContext(AccountContext);

  useEffect(() => {
    getSubscriptionProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Subscription State Management
  const [selectedSubscription, setSelectedSubscription] = useState("");
  const [selectedSubscriptionID, setSelectedSubscriptionID] = useState("");
  // eslint-disable-next-line
  const [currency, setCurrency] = useState();
  // eslint-disable-next-line
  const [reloadTrigger, setReloadTrigger] = useState(Date.now());

  const [membershipDetails, , state] = usePromise(getMembershipDetails, [
    reloadTrigger,
  ]);

  // eslint-disable-next-line
  const loading = state === "pending";

  const handleMembershipChange = () => {
    setActiveStep(0);
  };

  useEffect(() => {
    if (membershipDetails && membershipDetails.currency) {
      setCurrency(membershipDetails.currency.toUpperCase());
    } else {
      setCurrency("GBP");
    }
  }, [membershipDetails]);

  /* ==============================================
  =========== Subscription Handlers ===============
  ============================================== */

  // Handle Currency Change
  const handleCurrencyChange = (event) => {
    setSelectedSubscription("");
    setSelectedSubscriptionID("");
    setCurrency(event.target.value);
  };

  function onHandleSubscription(subscription) {
    setSelectedSubscription(subscription);
    setSelectedSubscriptionID(subscription.id);
  }

  /* ==============================================
  =========== Payment Method Handlers =============
  ============================================== */

  // const handleCreditCardSelected = () => {
  //   setPaymentMethodSelected(true);
  //   setPaymentMethodSelectedPayPal(false);
  //   setPaymentMethodSelectedCreditCard(true);
  // };

  // const handlePayPalSelected = () => {
  //   if (selectedSubscription.paypalReference === "annual52") {
  //     // setPaymentMethodSelected(true);
  //     setPaymentMethodSelectedCreditCard(false);
  //     setPaymentMethodSelectedPayPal(true);
  //   } else {
  //     alert("PayPal Payment not available for FarmFLiX Monthly Subscription.");
  //   }
  // };

  /* ==============================================
  ============= Payment Handlers ==================
  ============================================== */

  const [activeStep, setActiveStep] = React.useState(0);
  const [skipped, setSkipped] = React.useState(new Set());
  // const steps = getSteps();

  const isStepSkipped = (step) => {
    return skipped.has(step);
  };

  const handleNext = () => {
    resetErrors();
    let newSkipped = skipped;
    if (isStepSkipped(activeStep)) {
      newSkipped = new Set(newSkipped.values());
      newSkipped.delete(activeStep);
    }

    setActiveStep((prevActiveStep) => prevActiveStep + 1);
    setSkipped(newSkipped);
  };

  const tryAgain = () => {
    resetPaymentErrors();
    setActiveStep(0);
  };

  function getStepContent(step) {
    switch (step) {
      case 0:
        return <Step1Subscriptions />;
      case 1:
        return (
          <>
            <Step2Payment />
          </>
        );
      default:
        return "Unknown step";
    }
  }

  // Step 1 - Show Membership Plans
  function Step1Subscriptions() {
    return (
      <>
        {membershipDetails && (
          <>
            <Container style={{ padding: 0 }}>
              <Grid container sx={v4GridContainerWidthSx}>
                <Grid
                  size={{ xs: 12, sm: 8, md: 6 }}
                  sx={subscriptionWrapperItemSx}
                >
                  <Typography variant="body2" sx={bodytextSx}>
                    Step <b>1</b> of <b>2</b>
                  </Typography>
                  {membershipDetails.stripeCustomerId && (
                    <Typography variant="h2" sx={subtitleSx}>
                      Choose your Membership
                    </Typography>
                  )}
                  {!membershipDetails.stripeCustomerId && (
                    <Typography variant="h2" sx={subtitleSx}>
                      Choose your Currency
                    </Typography>
                  )}
                  <Grid container sx={v4GridContainerWidthSx}>
                    <Grid size={{ xs: 8 }}>
                      <Typography variant="body1" sx={bodytextSx}>
                        Select a FarmFLiX Membership.
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 4 }} align="right">
                      {!membershipDetails.stripeCustomerId && (
                        /* ♦ PARITY RESTORE: v4's `Select` defaulted to
                           `variant="standard"`; v5+ made "outlined" the
                           default, which would swap the underline control for
                           a notched-border one and route themeV5's
                           `MuiInput.underline` overrides to a component that is
                           no longer rendered. Same delta as `TextField` in
                           guide §2. */
                        <Select
                          variant="standard"
                          value={currency}
                          onChange={handleCurrencyChange}
                        >
                          <MenuItem value={"GBP"}>
                            <b>&#xa3; </b> GBP
                          </MenuItem>
                          <MenuItem value={"EUR"}>
                            <b>&#8364; </b>EUR
                          </MenuItem>
                          <MenuItem value={"USD"}>
                            <b>&#36; </b> USD
                          </MenuItem>
                        </Select>
                      )}
                    </Grid>
                  </Grid>
                  {subscriptionProducts && (
                    <Grid container sx={subscriptionWrapperContainerSx}>
                      {subscriptionProducts.results
                        .filter(
                          (subscription) =>
                            subscription.currency === currency &&
                            subscription.cost > 0,
                        )
                        .map((subscription, index) => (
                          <Grid
                            size={{ xs: 12, sm: 12, md: 12 }}
                            key={index}
                            sx={
                              subscription.id === selectedSubscriptionID
                                ? selectedSubscriptionContainerSx
                                : subscriptionContainerSx
                            }
                            onClick={() => onHandleSubscription(subscription)}
                          >
                            <Grid container sx={v4GridContainerWidthSx}>
                              <Grid size={{ xs: 7 }}>
                                <Typography sx={subscriptionNameSx}>
                                  {subscription.name}
                                </Typography>
                                <Typography sx={subscriptionDescriptionSx}>
                                  {subscription.description}
                                </Typography>

                                {/* Price */}
                                <Typography
                                  sx={subscriptionPriceSx}
                                  variant="body1"
                                >
                                  <SubscriptionCostSpan>
                                    {CurrencySymbol({
                                      currency: subscription.currency,
                                    })}
                                    {subscription.cost}
                                  </SubscriptionCostSpan>
                                  <SubscriptionTermSpan>
                                    /{subscription.entitlementPeriodUnit}
                                  </SubscriptionTermSpan>
                                </Typography>
                              </Grid>
                              <Grid size={{ xs: 5 }} align="right">
                                {subscription.id === selectedSubscriptionID ? (
                                  <Button
                                    color="primary"
                                    disableRipple={true}
                                    sx={selectButtonSx}
                                    startIcon={<CheckCircleIcon />}
                                  >
                                    SELECTED
                                  </Button>
                                ) : (
                                  <Button
                                    color="primary"
                                    disableRipple={true}
                                    disableFocusRipple={true}
                                    sx={selectButtonUncheckedSx}
                                    startIcon={
                                      <RadioButtonUncheckedOutlinedIcon />
                                    }
                                  >
                                    SELECT
                                  </Button>
                                )}
                              </Grid>
                            </Grid>
                          </Grid>
                        ))}
                      <Grid size={{ xs: 12, sm: 12, md: 12 }}>
                        {activeStep === 0 && (
                          <Button
                            fullwidth="true"
                            onClick={handleNext}
                            sx={continueButtonSx}
                            disabled={!selectedSubscription}
                          >
                            Continue To Payment
                          </Button>
                        )}
                      </Grid>
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Container>
          </>
        )}
      </>
    );
  }

  function Step2Payment() {
    return (
      <>
        <Container style={{ padding: 0 }}>
          <Grid container sx={v4GridContainerWidthSx}>
            <Grid
              size={{ xs: 12, sm: 8, md: 6 }}
              sx={subscriptionWrapperItemSx}
            >
              <Typography variant="body2" sx={bodytextSx}>
                Step <b>2</b> of <b>2</b>
              </Typography>
              <Typography variant="h2" sx={subtitleSx}>
                Pay with Credit/Debit Card
              </Typography>
              {!paymentPending && (
                <>
                  <Typography sx={selectedMembershipLabelSx} variant="body1">
                    Membership Information
                  </Typography>
                  <Box sx={selectedMembershipSx}>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography
                        variant="body1"
                        sx={selectedMembershipTitleSx}
                      >
                        {selectedSubscription.name}
                        <br />
                        <SelectedMembershipPriceSpan>
                          {CurrencySymbol({
                            currency: selectedSubscription.currency,
                          })}
                          {selectedSubscription.cost}
                        </SelectedMembershipPriceSpan>
                        <SelectedMembershipTermSpan>
                          /{selectedSubscription.entitlementPeriodUnit}
                        </SelectedMembershipTermSpan>
                      </Typography>
                    </Box>
                    <Box>
                      <Typography
                        variant="body1"
                        sx={selectedMembershipChangeSx}
                        onClick={handleMembershipChange}
                      >
                        Change
                      </Typography>
                    </Box>
                  </Box>
                  <Divider sx={smlDividerSx} />
                  <MembershipCardForm Subscription={selectedSubscription} />
                </>
              )}
            </Grid>
          </Grid>
        </Container>
      </>
    );
  }

  return (
    <div>
      <Container style={{ padding: 0 }}>
        <Grid container sx={paperContainerSx}>
          <Grid size={{ xs: 12, sm: 12, md: 12 }} sx={v4GridItemSx}>
            {!paymentSuccess &&
              !paymentPending &&
              !paymentFailure &&
              !paymentUnableProcess &&
              getStepContent(activeStep)}

            {paymentPending && (
              <Box sx={responseBoxSx}>
                <CircularProgress color="primary" />
                <Typography
                  variant="h2"
                  sx={subtitleSx}
                  style={{ textAlign: "center" }}
                >
                  We&apos;re hitching your trailer on now...
                </Typography>
                <Typography
                  variant="h3"
                  sx={thirdTitleSx}
                  style={{ textAlign: "center" }}
                >
                  Payment processing...
                </Typography>
                <Typography
                  variant="body2"
                  sx={bodytextSx}
                  style={{ textAlign: "center" }}
                >
                  Please do not reload or refresh your browser.
                </Typography>
              </Box>
            )}
            {paymentFailure && (
              <Box sx={responseBoxSx}>
                <CancelIcon sx={responseIconSx} color="error" />
                <Typography
                  variant="h2"
                  sx={subtitleSx}
                  style={{ textAlign: "center" }}
                >
                  Payment Failed
                </Typography>
                <Typography
                  variant="body2"
                  sx={bodytextSx}
                  style={{ textAlign: "center" }}
                >
                  We were unable to charge your card for your FarmFLiX
                  Membership. Please try again.
                </Typography>
                <Button
                  size="medium"
                  onClick={tryAgain}
                  sx={buttonColourRestoreSx}
                >
                  Try Again
                </Button>
                <Divider sx={dividerSx} />
                <Typography variant="body2" sx={responseHelpSx}>
                  <b>Need help?</b> Contact us using the &apos;Help&apos; Widget
                  or email us at{" "}
                  <a href="mailto:info@farmflix.tv">info.farmflix.tv</a>
                </Typography>
              </Box>
            )}
            {paymentSuccess && (
              <Box sx={responseBoxSx}>
                <CheckCircleIcon sx={responseIconSx} color="primary" />
                <Typography component="h1" variant="h1" sx={upgradeTitleSx}>
                  Woo hoo!
                </Typography>
                <Typography
                  variant="h2"
                  sx={thirdTitleSx}
                  style={{ textAlign: "center" }}
                >
                  Payment Successfully Processed!
                </Typography>
                <Typography
                  variant="body1"
                  sx={bodytextSx}
                  style={{ textAlign: "center" }}
                >
                  Thank you for your payment. Your FarmFLiX Account is now set
                  up.
                </Typography>
                <a href="/videos" style={{ textDecoration: "none" }}>
                  <Button name="get-started" sx={buttonColourRestoreSx}>
                    Get Started
                  </Button>
                </a>
              </Box>
            )}
            {paymentUnableProcess && (
              <Box sx={responseBoxSx}>
                <ErrorIcon sx={responseIconSx} style={{ fill: "#ff9f00" }} />
                <Typography
                  variant="h2"
                  sx={subtitleSx}
                  style={{ textAlign: "center" }}
                >
                  Unable to Process Payment
                </Typography>
                <Typography
                  variant="body2"
                  sx={bodytextSx}
                  style={{ textAlign: "center" }}
                >
                  Unfortunately we cannot process your payment at this time.
                </Typography>
                <Button
                  size="medium"
                  onClick={tryAgain}
                  sx={buttonColourRestoreSx}
                >
                  Try Again
                </Button>
                <Divider sx={dividerSx} />
                <Typography variant="body2" sx={responseHelpSx}>
                  <b>Need help?</b> Contact us using the &apos;Help&apos; Widget
                  or email us at{" "}
                  <a href="mailto:info@farmflix.tv">info.farmflix.tv</a>
                </Typography>
              </Box>
            )}
          </Grid>
        </Grid>
      </Container>
    </div>
  );
};

export default UpgradeFormNew;
